<template>
  <div class="particle-viz-3d">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex><strong>公式：</strong>详见下方原理说明</div>
    </div>

    <div ref="canvasContainer" class="canvas-3d-box">
      <!-- Three.js Canvas will be injected here -->
      <div class="overlay-info">
        <div class="metric">
          <label>距离:</label>
          <span>1.496 × 10⁸ km</span>
        </div>
        <div class="metric">
          <label>状态:</label>
          <span :class="{ 'highlight': isParticle }">{{ isParticle ? 'POINT PARTICLE' : 'MACRO BODY' }}</span>
        </div>
      </div>
    </div>

    <div class="explanation-panel">
      <div class="glass-card">
        <i class="el-icon-info"></i>
        <p v-if="!isParticle">当前模型考虑地球的<strong>自转轴、形状及表面特征</strong>。在研究昼夜交替等现象时，物体的大小不可忽略。</p>
        <p v-else>物体被简化为一个<strong>几何点</strong>。当我们研究其公转轨迹时，形状因素对规律描述已无任何影响。</p>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'ParticleViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      isParticle: false,
      running: true
    }
  },
  mounted() {
    this.initThree();
  },
  beforeDestroy() {
    this.running = false;
    if (this.renderer) {
      this.renderer.dispose();
      this.canvasContainer.removeChild(this.renderer.domElement);
    }
  },
  methods: {
    initThree() {
      this.canvasContainer = this.$refs.canvasContainer;
      const width = this.canvasContainer.clientWidth;
      const height = this.canvasContainer.clientHeight;

      // Scene
      this.scene = new THREE.Scene();

      // Camera
      this.camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
      this.camera.position.z = 100;
      this.camera.position.y = 40;
      this.camera.lookAt(0, 0, 0);

      // Renderer
      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.canvasContainer.appendChild(this.renderer.domElement);

      // Lights
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
      this.scene.add(ambientLight);

      const sunLight = new THREE.PointLight(0xff9f43, 2, 300);
      sunLight.position.set(0, 0, 0);
      this.scene.add(sunLight);

      // Sun
      const sunGeo = new THREE.SphereGeometry(15, 32, 32);
      const sunMat = new THREE.MeshPhongMaterial({
        color: 0xff9f43,
        emissive: 0xff4d00,
        emissiveIntensity: 0.5
      });
      this.sun = new THREE.Mesh(sunGeo, sunMat);
      this.scene.add(this.sun);

      // Orbit
      const orbitCurve = new THREE.EllipseCurve(0, 0, 70, 70, 0, 2 * Math.PI, false, 0);
      const points = orbitCurve.getPoints(100);
      const orbitGeo = new THREE.BufferGeometry().setFromPoints(points);
      const orbitMat = new THREE.LineBasicMaterial({ color: 0x333333, transparent: true, opacity: 0.3 });
      this.orbitLine = new THREE.Line(orbitGeo, orbitMat);
      this.orbitLine.rotation.x = Math.PI / 2;
      this.scene.add(this.orbitLine);

      // Earth Group (for rotation)
      this.earthGroup = new THREE.Group();
      this.scene.add(this.earthGroup);

      // Earth Mesh
      const earthGeo = new THREE.SphereGeometry(6, 32, 32);
      const earthMat = new THREE.MeshPhongMaterial({
        color: 0x2233ff,
        specular: 0x555555,
        shininess: 25
      });
      this.earth = new THREE.Mesh(earthGeo, earthMat);
      this.earthGroup.add(this.earth);

      // Particle Mesh (Hidden initially)
      const partGeo = new THREE.SphereGeometry(1, 16, 16);
      const partMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
      this.particle = new THREE.Mesh(partGeo, partMat);
      this.particle.visible = false;
      this.earthGroup.add(this.particle);

      // Glow effect for particle
      const spriteMat = new THREE.SpriteMaterial({
        map: this.createGlowTexture(),
        color: 0xffffff,
        transparent: true,
        blending: THREE.AdditiveBlending
      });
      this.glow = new THREE.Sprite(spriteMat);
      this.glow.scale.set(6, 6, 1);
      this.glow.visible = false;
      this.earthGroup.add(this.glow);

      this.animate();
    },
    createGlowTexture() {
      const canvas = document.createElement('canvas');
      canvas.width = 64;
      canvas.height = 64;
      const ctx = canvas.getContext('2d');
      const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      gradient.addColorStop(0, 'rgba(255,255,255,1)');
      gradient.addColorStop(0.2, 'rgba(0,210,255,0.5)');
      gradient.addColorStop(0.4, 'rgba(0,110,253,0.2)');
      gradient.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 64, 64);
      const texture = new THREE.Texture(canvas);
      texture.needsUpdate = true;
      return texture;
    },
    toggleParticle() {
      this.isParticle = !this.isParticle;
      this.earth.visible = !this.isParticle;
      this.particle.visible = this.isParticle;
      this.glow.visible = this.isParticle;
    },
    animate() {
      if (!this.running) return;
      requestAnimationFrame(this.animate);

      const time = Date.now() * 0.0005;
      
      // Orbiting
      const radius = 70;
      this.earthGroup.position.x = Math.cos(time) * radius;
      this.earthGroup.position.z = Math.sin(time) * radius;

      // Rotation
      if (this.earth.visible) {
        this.earth.rotation.y += 0.02;
      }

      this.renderer.render(this.scene, this.camera);
    }
  }
}
</script>

<style scoped lang="scss">
.particle-viz-3d {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  color: #fff;
}

.canvas-3d-box {
  flex: 1;
  min-height: 400px;
  background: radial-gradient(circle at center, #0a0a20 0%, #000 100%);
  border-radius: 16px;
  position: relative;
  overflow: hidden;

  .overlay-info {
    position: absolute;
    top: 20px; left: 20px;
    display: flex; flex-direction: column; gap: 10px;
    pointer-events: none;

    .metric {
      background: rgba(0, 0, 0, 0.4);
      padding: 8px 15px;
      border-radius: 8px;
      border-left: 3px solid #00d2ff;
      font-family: monospace;
      
      label { font-size: 0.65rem; color: #888; display: block; margin-bottom: 2px; }
      span { font-size: 0.85rem; color: #00d2ff; }
      .highlight { color: #fff; text-shadow: 0 0 10px #00d2ff; }
    }
  }
}

.explanation-panel {
  .glass-card {
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 16px;
    padding: 20px;
    display: flex; gap: 15px; align-items: flex-start;

    i { font-size: 1.5rem; color: #00d2ff; margin-top: 2px; }
    p { margin: 0; font-size: 0.9rem; line-height: 1.6; color: #ccc; }
    strong { color: #fff; }
  }
}

.glow-btn {
  background: linear-gradient(135deg, #006efd, #00d2ff) !important;
  border: none !important;
  box-shadow: 0 4px 15px rgba(0, 110, 253, 0.4);
  font-weight: bold;
  letter-spacing: 1px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 110, 253, 0.6);
  }
}

.viz-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  .title { font-size: 1.2rem; font-weight: 300; color: #fff; }
}
</style>
